import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import EditUserModal from "./EditUserModal";
import Pagination from "../../../Pagination";
import { Helmet } from "react-helmet-async";
import AnalyticsTracker from "../../../GASetup/AnalyticsTracker";
import Filter from "./Filters/Filter";
import LoadingAnimation from "../../../Animations/LoadingAnimation";
import FormatTime from "../../../AdsPage/FormatTime";
import { useNavigate } from "react-router-dom";
import {
  cardOutline,
  fonts,
  headingFonts,
  noOutline,
} from "../../../../Styles/Styles";
import SendVerificationEmails from "./SendVerificationEmails";

export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [roles, setRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [selectedRole, setSelectedRole] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [emailVerified, setEmailVerified] = useState("");
  const [idVerified, setIdVerified] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phoneNumber: "",
    countryCode: "",
    subscribeNewsletter: false,
    isVerified: false,
    role: "",
    organization: "",
    isVerifiedbyID: "",
  });

  // Fetch users from API
  const fetchUsers = async (page) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/users/allusers?page=${page}&role=${selectedRole}&isVerified=${emailVerified}&isVerifiedbyID=${idVerified}&searchQuery=${searchQuery}&sortOrder=${sortOrder}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch users");
      const data = await response.json();
      setUsers(data.users);
      setTotalUsers(data.totalUsers);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const fetchRoles = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/admin/roles`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      if (!response.ok) throw new Error("Failed to fetch roles");
      const data = await response.json();
      setRoles(data.roles);
    } catch (err) {
      console.error("Error fetching roles:", err.message);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Set up debounce for search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Delay 500ms after the user stops typing

    return () => clearTimeout(timer); // Clean up timer on each render
  }, [searchQuery]);

  useEffect(() => {
    fetchUsers(currentPage);
    console.log(selectedRole);
  }, [
    currentPage,
    selectedRole,
    emailVerified,
    idVerified,
    debouncedSearchQuery,
    sortOrder,
  ]);

  const handleEditUser = (user) => {
    setEditingUser(user._id);
    setEditFormData({ ...user });
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveEdit = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/users/edituser/${editingUser}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(editFormData),
        },
      );
      if (!response.ok) {
        throw new Error("Failed to update user");
      }

      const updatedUser = await response.json();
      if (updatedUser) {
        setSuccessMessage(updatedUser.message);
      }
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === editingUser ? updatedUser.user : user,
        ),
      );
      setEditingUser(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/users/deleteuser/${userId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to delete user");
        }

        const deletedUserResponse = await response.json();
        alert(deletedUserResponse.message);

        // Update the users state to remove the deleted user
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user._id !== userId),
        );
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleBlockUser = async (userId) => {
    if (window.confirm("Are you sure you want to block this user?")) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/users/blockuser/${userId}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error("Failed to block user");
        }

        const blockedUserResponse = await response.json();
        alert(blockedUserResponse.message);

        // Update the users state to reflect the blocked user
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === userId ? { ...user, isBlocked: true } : user,
          ),
        );
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value); // Update searchQuery
  };

  const handleSortOrderChange = (newSortOrder) => {
    setSortOrder(newSortOrder);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleViewUserProfile = (id) => {
    navigate(`/profile/${id}`);
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>Users Management - Listed.ky</title>
        <meta name="description" content="Users Management - Listed.ky" />
      </Helmet>
      <AnalyticsTracker />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
          User Management ({totalUsers})
        </Typography>

        <SendVerificationEmails />
      </Box>
      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginBottom: "1rem",
          width: "100%",
        }}
      >
        <Filter
          roles={roles}
          selectedRole={selectedRole}
          onRoleChange={setSelectedRole}
          emailVerified={emailVerified}
          idVerified={idVerified}
          onEmailVerifiedChange={setEmailVerified}
          onIdVerifiedChange={setIdVerified}
          searchQuery={searchQuery} // Pass the searchQuery (or debouncedSearchQuery)
          onSearchChange={handleSearchChange} // Keep the search change handler
          sortOrder={sortOrder} // Pass sortOrder
          onSortOrderChange={handleSortOrderChange}
        />
      </div>
      {error && (
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity="error">
            {error}
          </Alert>
        </Snackbar>
      )}
      {successMessage && (
        <Snackbar
          open={Boolean(successMessage)}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity="success">
            {successMessage}
          </Alert>
        </Snackbar>
      )}
      <TableContainer sx={{ ...cardOutline }}>
        <Table sx={{ minWidth: 650 }} aria-label="user table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headingFonts }}>Full Name</TableCell>
              <TableCell sx={{ ...headingFonts }}>Email</TableCell>
              <TableCell sx={{ ...headingFonts }}>Phone Number</TableCell>
              <TableCell sx={{ ...headingFonts }}>Created Date</TableCell>
              <TableCell sx={{ ...headingFonts }}>Role</TableCell>
              <TableCell sx={{ ...headingFonts }}>Ads Posted</TableCell>
              <TableCell sx={{ ...headingFonts }}>Email Verified</TableCell>
              <TableCell sx={{ ...headingFonts }}>ID Verified</TableCell>
              <TableCell sx={{ ...headingFonts }}>Active</TableCell>
              <TableCell sx={{ ...headingFonts }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user._id}
                sx={{
                  backgroundColor: user.isBlocked ? "#f8d7da" : "transparent",
                }}
              >
                <TableCell
                  onClick={() => handleViewUserProfile(user._id)}
                  sx={{ ...headingFonts, cursor: "pointer" }}
                >
                  {`${user.firstName} ${user.lastName}`}
                </TableCell>
                <TableCell sx={{ ...fonts }}>
                  {user.email.split("@")[0]}@***
                </TableCell>
                <TableCell sx={{ ...fonts }}>
                  {user.countryCode + user.phoneNumber}
                </TableCell>
                <TableCell sx={{ ...fonts }}>
                  <FormatTime createdAt={user.createdAt} />
                </TableCell>
                <TableCell sx={{ ...fonts }}>{user.role}</TableCell>
                <TableCell sx={{ ...fonts }}>{user.adsCount}</TableCell>
                <TableCell>{user.isVerified ? "✅" : "❌"}</TableCell>
                <TableCell sx={{ ...fonts }}>{user.isVerifiedbyID}</TableCell>
                <TableCell sx={{ ...fonts }}>
                  {user.isBlocked ? (
                    "Blocked"
                  ) : user?.loginActivity?.length ? (
                    <FormatTime
                      createdAt={
                        user.loginActivity[user.loginActivity.length - 1]
                          .timestamp
                      }
                    />
                  ) : (
                    "❌"
                  )}
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      onClick={() => handleEditUser(user)}
                      sx={{
                        color: "var(--primary-color)",
                        ...noOutline,
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteUser(user._id)}
                      color="error"
                      sx={{ ...noOutline }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleBlockUser(user._id)}
                      color="warning"
                      sx={{ ...noOutline }}
                      disabled={user.isBlocked} // Disable if already blocked
                    >
                      <BlockIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
      <EditUserModal
        isSubmitting={isSubmitting}
        editFormData={editFormData}
        handleEditFormChange={handleEditFormChange}
        handleSaveEdit={handleSaveEdit}
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
      />
    </Box>
  );
}
